import "./styles.css";
import React, { useState, useRef, useEffect } from "react";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { format, endOfYear } from "date-fns";
import { FaCalendarAlt } from "react-icons/fa";

const DateRangePickerComponent = ({
  state,
  setState,
  applyDateRange,
  width = "",
}) => {
  const { startDate, endDate } = state.selection;
  const maxDate = endOfYear(new Date());
  const formattedStartDate = format(startDate, "yyyy-MM-dd");
  const formattedEndDate = format(endDate, "yyyy-MM-dd");
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const pickerRef = useRef(null);
  const prevDatesRef = useRef({ start: formattedStartDate, end: formattedEndDate });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const togglePicker = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
    } else {
      setIsPickerOpen((prevState) => !prevState);
    }
  };

  const handleClickOutside = (event) => {
    if (pickerRef.current && !pickerRef.current.contains(event.target)) {
      setIsPickerOpen(false);
    }
  };

  useEffect(() => {
    if (!isMobile) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [isMobile]);

  const handleDateChange = (item) => {
    setState((prevState) => ({
      ...prevState,
      selection: {
        ...prevState.selection,
        ...item.selection,
      },
    }));
  };

  const handleApplyDateRange = () => {
    if (state.selection.startDate && state.selection.endDate) {
      applyDateRange(state.selection.startDate, state.selection.endDate);
      setIsPickerOpen(false);
    }
  };

  const handleStartDateChange = (event) => {
    const dateValue = event.target.value;
    // Create date at midnight in local timezone
    const [year, month, day] = dateValue.split("-").map(Number);
    const newStartDate = new Date(year, month - 1, day, 0, 0, 0, 0);
    setState((prevState) => ({
      ...prevState,
      selection: {
        ...prevState.selection,
        startDate: newStartDate,
      },
    }));
  };

  const handleEndDateChange = (event) => {
    const dateValue = event.target.value;
    // Create date at midnight in local timezone
    const [year, month, day] = dateValue.split("-").map(Number);
    const newEndDate = new Date(year, month - 1, day, 0, 0, 0, 0);
    setState((prevState) => ({
      ...prevState,
      selection: {
        ...prevState.selection,
        endDate: newEndDate,
      },
    }));
  };

  // Auto-apply date range when dates change in mobile view
  useEffect(() => {
    // Only apply if dates actually changed (not on initial mount)
    const datesChanged = 
      prevDatesRef.current.start !== formattedStartDate || 
      prevDatesRef.current.end !== formattedEndDate;
    
    if (isMobile && datesChanged && state.selection.startDate && state.selection.endDate) {
      prevDatesRef.current = { start: formattedStartDate, end: formattedEndDate };
      applyDateRange();
    } else {
      prevDatesRef.current = { start: formattedStartDate, end: formattedEndDate };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formattedStartDate, formattedEndDate, isMobile]);

  if (isMobile) {
    return (
      <div style={{ width: width, display: "flex", gap: "8px", flexDirection: "column" }}>
        <input
          type="date"
          value={formattedStartDate}
          onChange={handleStartDateChange}
          max={format(maxDate, "yyyy-MM-dd")}
          min="2000-01-01"
          className="form-control"
          style={{
            border: "rgba(89, 198, 201, 1) 1px solid",
            paddingTop: "6px",
            paddingBottom: "6px",
          }}
        />
        <input
          type="date"
          value={formattedEndDate}
          onChange={handleEndDateChange}
          max={format(maxDate, "yyyy-MM-dd")}
          min="2000-01-01"
          className="form-control"
          style={{
            border: "rgba(89, 198, 201, 1) 1px solid",
            paddingTop: "6px",
            paddingBottom: "6px",
          }}
        />
      </div>
    );
  }

  return (
    <div ref={pickerRef} style={{ width: width, position: "relative" }}>
      <button
        type="button"
        className="btn d-flex align-items-center justify-content-between px-1"
        onClick={togglePicker}
        onKeyDown={togglePicker}
        style={{
          border: "rgba(89, 198, 201, 1) 1px solid",
          width: width,
          paddingTop: "6px",
          paddingBottom: "6px",
        }}
      >
        {`${formattedStartDate} - ${formattedEndDate}`}
        <FaCalendarAlt className="me-1" />
      </button>

      {isPickerOpen && (
        <div
          dir="ltr"
          className="mt-3 shadow border bg-light date-picker-dropdown"
          style={{ zIndex: 9999 }}
        >
          <DateRangePicker
            onChange={handleDateChange}
            showSelectionPreview={false}
            moveRangeOnFirstSelection={false}
            months={2}
            ranges={[state.selection]}
            maxDate={maxDate}
            minDate={new Date("2000-01-01")}
            direction="horizontal"
            ariaLabels={{
              dateInput: {
                selection: {
                  startDate: `Start Date: ${formattedStartDate}`,
                  endDate: `End Date: ${formattedEndDate}`,
                },
              },
              monthPicker: "month picker",
              yearPicker: "year picker",
              prevButton: "previous month button",
              nextButton: "next month button",
            }}
          />
          <div className="d-flex align justify-content-end px-2">
            <button
              className="btn text-white mt-3 mb-2"
              style={{ background: "rgba(89, 198, 201, 1)" }}
              onClick={handleApplyDateRange}
            >
              ok
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePickerComponent;